import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Package, Truck, Calendar, Info, CheckCheck } from 'lucide-react';
import {
  useNotificatieCount,
  useNotificaties,
  useMarkeerGelezen,
  useMarkeerAllesGelezen,
} from '../hooks/useNotificaties.js';
import { KIcon } from '../../../core/shared/components/KIcon.js';
import { useSocketEvent } from '../../../core/shared/hooks/useSocket.js';

function tijdGeleden(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, now - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'zojuist';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min geleden`;
  const uur = Math.floor(min / 60);
  if (uur < 24) return `${uur} uur geleden`;
  const dag = Math.floor(uur / 24);
  if (dag === 1) return 'gisteren';
  return `${dag} dagen geleden`;
}

function TypeIcoon({ type }: { type: string }) {
  switch (type) {
    case 'DATABUS_EVENT':
      return <Package size={14} className="text-blue-500 flex-shrink-0" />;
    case 'DOSSIER_UPDATE':
      return <Truck size={14} className="text-green-500 flex-shrink-0" />;
    case 'AFSPRAAK':
    case 'PLANNING':
      return <Calendar size={14} className="text-amber-500 flex-shrink-0" />;
    case 'BERICHT':
      return <Bell size={14} className="text-brand flex-shrink-0" />;
    case 'VOORRAAD':
      return <Package size={14} className="text-purple-500 flex-shrink-0" />;
    default:
      return <Info size={14} className="text-gray-400 flex-shrink-0" />;
  }
}

export function NotificatieBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { data: countData } = useNotificatieCount();
  const { data: notificaties } = useNotificaties({ limit: 20 });
  const markeerGelezen = useMarkeerGelezen();
  const markeerAlles = useMarkeerAllesGelezen();

  const ongelezen = countData?.ongelezen ?? 0;

  // Real-time: invalidate cache bij nieuwe notificatie via Socket.io
  const handleNewNotificatie = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notificaties'] });
  }, [queryClient]);
  useSocketEvent('notificatie:new', handleNewNotificatie);

  // Sluit panel bij click buiten
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleItemClick = (n: { id: string; gelezen: boolean; dossierId?: string; actionUrl?: string }) => {
    if (!n.gelezen) {
      markeerGelezen.mutate(n.id);
    }
    if (n.actionUrl) {
      navigate(n.actionUrl);
    } else if (n.dossierId) {
      navigate(`/dossiers/${n.dossierId}`);
    }
    setOpen(false);
  };

  return (
    <div ref={panelRef} className="relative">
      {/* Bell button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={ongelezen > 0 ? `Meldingen, ${ongelezen} ongelezen` : 'Meldingen'}
        className="relative min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg text-gray-700 hover:bg-white transition-colors"
      >
        <KIcon naam="melding" />
        {ongelezen > 0 && (
          <span aria-hidden="true" className="absolute top-1 right-0 bg-brand text-[13px] font-semibold px-1.5 rounded-full min-w-[20px] text-center leading-5" style={{ color: 'var(--k-on-brand)' }}>
            {ongelezen > 99 ? '99+' : ongelezen}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Meldingen</span>
            {ongelezen > 0 && (
              <button
                onClick={() => markeerAlles.mutate()}
                className="flex items-center gap-1 text-xs text-gold hover:text-gold/80 transition-colors"
              >
                <CheckCheck size={13} />
                Alles gelezen
              </button>
            )}
          </div>

          {/* Lijst */}
          <div className="max-h-72 overflow-y-auto">
            {(!notificaties || notificaties.length === 0) ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                Geen meldingen
              </div>
            ) : (
              notificaties.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors hover:bg-gray-50 ${
                    !n.gelezen ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex gap-2.5">
                    <div className="mt-0.5">
                      <TypeIcoon type={n.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <span className={`text-sm leading-tight ${!n.gelezen ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                          {n.titel}
                        </span>
                        {!n.gelezen && (
                          <span className="w-2 h-2 mt-1 rounded-full bg-gold flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.bericht}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{tijdGeleden(n.createdAt)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer: link naar volledige pagina */}
          <button
            onClick={() => { navigate('/notificaties'); setOpen(false); }}
            className="w-full px-4 py-2.5 text-xs font-medium text-gold hover:bg-gray-50 border-t border-gray-100 transition-colors text-center"
          >
            Alle meldingen bekijken
          </button>
        </div>
      )}
    </div>
  );
}
